import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  useAnalyzeEssay,
  useSaveDraft,
  useUpdateTokens,
} from '@/hooks/useTextAnalyzer';
import { useAuth } from '@/features/auth/context/AuthContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import InfoBox from './InfoBox';
import DraftButton from './DraftButton';
import WordCountModal from './WordCountModal';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import { SpellCheckMatch } from '@/services/spellCheckerService';
import { showApiError, showValidationError } from '@/utils/toast';
import { AnalysisData } from '@/types';

interface EssayFormProps {
  highlightedWord?: { offset: number; length: number } | null;
  spellCheckResults?: SpellCheckMatch[];
  data?: AnalysisData;
  essayText: string;
  setEssayText: (value: string) => void;
  onAnalysisUpdate?: (analysis: any, updatedData: AnalysisData) => void;
}

const EssayForm: React.FC<EssayFormProps> = ({
  highlightedWord,
  spellCheckResults = [],
  data,
  essayText,
  setEssayText,
  onAnalysisUpdate

}) => {

  const navigate = useNavigate();
  const isResultPage = useLocation().pathname.includes('/result');


  const [isLoading, setIsLoading] = useState(false);
  const [showWordCountModal, setShowWordCountModal] = useState(false);

  const analyzeMutation = useAnalyzeEssay();


  const wordCount = essayText.trim().split(/\s+/).length;


  const handleSubmit = async () => {
    // NOTE: This is a temporary bypass to allow the user to submit an essay that is over the word count limit
    if (wordCount > Number(data?.wordCount ?? 0) && false) {
      setShowWordCountModal(true);
      return;
    }

    setIsLoading(true);
    try {

      const payload = {
        prompt_id: data?.promptId ?? 0,
        essay: essayText,
        university_id: data?.universityId ?? 0,
        ...(data?.draftId && { draft_id: parseInt(data?.draftId) }),
      };

      console.log('Analysis payload:', payload);
      const analysis = await analyzeMutation.mutateAsync(payload);

      if (!isResultPage) {
        data!.essay = essayText;
        data!.draftStatus = "ACTIVE";
        navigate(`/result/${data?.universityId}`, {
          state: {
            analysis,
            data
          }
        });
      } else {
        // Update the current page's analysis data
        if (onAnalysisUpdate && data) {
          onAnalysisUpdate(analysis, { ...data, essay: essayText });
        }
      }
    }

    catch (error) {
      showApiError(error);
    } finally {
      setIsLoading(false);
    }

  }


  const handleModalClose = () => {
    setShowWordCountModal(false);
  };

  const getPlaceholderText = () => {
    if (!essayText) {
      return `Start writing your essay here. You have ${data?.wordCount} words to work with.`;
    }
    return "Select a prompt from the sidebar to begin writing your essay...";
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="rounded-lg shadow-lg p-6 mb-4 overflow-y-auto short:max-h-[290px] w-full max-w-7xl" style={{ background: 'var(--surface-primary)' }} data-tour="essay-writing-area">
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit();
          }}
          className="form-container"
        >

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="col-span-2">
              <div className="form-item">
                <input
                  placeholder="Enter Essay Prompt"
                  value={data?.prompt || ''}
                  list="prompt-options"
                  disabled
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-hover)', color: 'var(--text-primary)' }}
                />
                <datalist id="prompt-options">
                </datalist>
              </div>
            </div>
            <div className="col-span-1">
              <div className="form-item">
                <input
                  placeholder="Word Count Limit"
                  value={data?.wordCount || ''}
                  disabled
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-hover)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>
          </div>

          {/* Guidance message when prompt is selected but no essay */}
          {data?.prompt && !data?.essay && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Ready to write!</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    You've selected a prompt. Start writing your essay below. The analyze button will be enabled once you begin typing.
                  </p>
                </div>
              </div>
            </div>
          )}


          <div className="form-item mb-4">
            <RichTextEditor
              value={essayText}
              onChange={setEssayText}
              spellCheckResults={spellCheckResults}
              highlightedWord={highlightedWord}
              placeholder={getPlaceholderText()}
            />
          </div>
          <div className="word-count mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {essayText ? wordCount : '0'} words

            <span className="ml-2" style={{ color: 'var(--text-tertiary)' }}>/ {data?.wordCount}</span>
          </div>


          <div className="submit-button flex gap-3 mb-6">
            <button
              type="submit"
              data-tour="analyze-button"
              disabled={Object.keys(data ?? {}).length === 0 || !essayText || isLoading}
              className={`submit-button px-6 py-2 rounded-md text-white font-medium transition-all duration-200 ${!essayText || isLoading || Object.keys(data ?? {}).length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow-md'
                }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Analyzing...
                </div>
              ) : (
                `Analyze`
              )}
            </button>
            {/* {isResultPage && (
              
            <DraftButton
              draftId={data?.draftId}
              draftStatus={data?.draftStatus}
            /> 
              )} */}
          </div>
        </form>
      </div>

      <InfoBox isLoading={isLoading} />
      <WordCountModal
        showWordCountModal={showWordCountModal}
        onCancel={handleModalClose}
      />
    </div>
  );
};

export default EssayForm;